import { useMemo, useState } from "react";
import {
  Box,
  TextField,
  Checkbox,
  FormControlLabel,
  Typography,
} from "@material-ui/core";
import SearchIcon from "@mui/icons-material/Search";
import StoreIcon from "@mui/icons-material/StoreOutlined";
import LayersIcon from "@mui/icons-material/LayersOutlined";
import WorkIcon from "@mui/icons-material/WorkOutline";
import PaletteIcon from "@mui/icons-material/PaletteOutlined";
import StraightenIcon from "@mui/icons-material/Straighten";
import FilterIcon from "@mui/icons-material/FilterAltOutlined";

function collectSizesFromFamily(fam) {
  const sizes = new Set();
  (fam.matrixSizes || []).forEach((s) => {
    const t = String(s || "").trim();
    if (t) sizes.add(t);
  });
  (fam.products || []).forEach((p) => {
    const t = String(p.tamanhoLabel || "").trim();
    if (t) sizes.add(t);
  });
  return sizes;
}

function collectColorsFromFamily(fam) {
  const colors = new Set();
  (fam.matrixColors || []).forEach((c) => {
    const t = String(c || "").trim();
    if (t && t !== "Geral") colors.add(t);
  });
  (fam.variants || []).forEach((v) => {
    const t = String(v || "").trim();
    if (t && t !== "Geral") colors.add(t);
  });
  (fam.products || []).forEach((p) => {
    const t = String(p.corLabel || "").trim();
    if (t && t !== "Geral") colors.add(t);
  });
  return colors;
}

function formatVitrineFilterLabel(vitrineKey, vitrinesMeta) {
  const meta = vitrinesMeta[vitrineKey] || {};
  const nome = (meta.nome || "").trim();
  const codigo = meta.idVitrineVirtualB2b;
  if (nome && codigo != null) return `${nome} · ${codigo}`;
  if (nome) return nome;
  return String(vitrineKey);
}

function FilterSection({
  title,
  icon: Icon,
  searchPlaceholder,
  searchTerm,
  onSearchChange,
  onInputFocus,
  onInputBlur,
  children,
  emptyMessage,
}) {
  return (
    <Box className="filter-section">
      <Typography className="filter-section-title">
        <Icon className="filter-icon" />
        {title}
      </Typography>
      {searchPlaceholder && (
        <Box className="search-input-wrapper-small">
          <FilterIcon className="search-icon" />
          <TextField
            fullWidth
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="search-input-small"
            variant="outlined"
            InputProps={{
              className: "search-input-inner",
            }}
            inputProps={{
              style: { paddingLeft: "28px", fontSize: "12px" },
            }}
            onFocus={onInputFocus}
            onBlur={onInputBlur}
          />
        </Box>
      )}
      <Box className="filter-options scrollable">
        {children}
        {emptyMessage && (
          <Typography className="no-results">{emptyMessage}</Typography>
        )}
      </Box>
    </Box>
  );
}

export default function Sidebar({
  currentBrand,
  families,
  selectedCategories,
  selectedCommercialNames,
  selectedVitrines,
  selectedSizes,
  selectedColors,
  productSearchTerm,
  setProductSearchTerm,
  toggleFilter,
  theme,
  vitrinesMeta = {},
}) {
  const [categorySearchTerm, setCategorySearchTerm] = useState("");
  const [commercialSearchTerm, setCommercialSearchTerm] = useState("");
  const [sizeSearchTerm, setSizeSearchTerm] = useState("");
  const [colorSearchTerm, setColorSearchTerm] = useState("");
  const [vitrineSearchTerm, setVitrineSearchTerm] = useState("");

  const brandFamilies = families[currentBrand] || {};
  const keys = Object.keys(brandFamilies);

  const categories = useMemo(() => {
    const cats = new Set();
    keys.forEach((k) => {
      const c = String(brandFamilies[k].category || "").trim();
      if (c) cats.add(c);
    });
    return Array.from(cats).sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [brandFamilies, keys]);

  const commercialNames = useMemo(() => {
    const comms = new Set();
    keys.forEach((k) => {
      const n = String(brandFamilies[k].commercialName || "").trim();
      if (n) comms.add(n);
    });
    return Array.from(comms).sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [brandFamilies, keys]);

  const sizes = useMemo(() => {
    const set = new Set();
    keys.forEach((k) => {
      collectSizesFromFamily(brandFamilies[k]).forEach((s) => set.add(s));
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [brandFamilies, keys]);

  const colors = useMemo(() => {
    const set = new Set();
    keys.forEach((k) => {
      collectColorsFromFamily(brandFamilies[k]).forEach((c) => set.add(c));
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [brandFamilies, keys]);

  const vitrines = useMemo(() => {
    const vits = new Set();
    keys.forEach((k) => {
      const fam = brandFamilies[k];
      if (fam.vitrine != null) vits.add(fam.vitrine);
      (fam.products || []).forEach((p) => {
        if (p.vitrine != null) vits.add(p.vitrine);
      });
    });
    return Array.from(vits).sort((a, b) => {
      const la = formatVitrineFilterLabel(a, vitrinesMeta);
      const lb = formatVitrineFilterLabel(b, vitrinesMeta);
      return la.localeCompare(lb, "pt-BR");
    });
  }, [brandFamilies, keys, vitrinesMeta]);

  const filterByTerm = (list, term) => {
    const t = term.trim().toLowerCase();
    if (!t) return list;
    return list.filter((item) => String(item).toLowerCase().includes(t));
  };

  const filteredCategories = useMemo(
    () => filterByTerm(categories, categorySearchTerm),
    [categories, categorySearchTerm],
  );

  const filteredCommercialNames = useMemo(
    () => filterByTerm(commercialNames, commercialSearchTerm),
    [commercialNames, commercialSearchTerm],
  );

  const filteredSizes = useMemo(
    () => filterByTerm(sizes, sizeSearchTerm),
    [sizes, sizeSearchTerm],
  );

  const filteredColors = useMemo(
    () => filterByTerm(colors, colorSearchTerm),
    [colors, colorSearchTerm],
  );

  const filteredVitrines = useMemo(() => {
    const t = vitrineSearchTerm.trim().toLowerCase();
    if (!t) return vitrines;
    return vitrines.filter((vKey) => {
      const meta = vitrinesMeta[vKey] || {};
      const nome = String(meta.nome || "").toLowerCase();
      const codigo = String(meta.idVitrineVirtualB2b ?? vKey).toLowerCase();
      const label = formatVitrineFilterLabel(vKey, vitrinesMeta).toLowerCase();
      return nome.includes(t) || codigo.includes(t) || label.includes(t);
    });
  }, [vitrines, vitrineSearchTerm, vitrinesMeta]);

  const handleInputFocus = (e) => {
    const inputRoot = e.target.closest(".MuiOutlinedInput-root");
    if (inputRoot) {
      const fieldset = inputRoot.querySelector("fieldset");
      if (fieldset) {
        fieldset.style.borderColor = theme.primary;
      }
    }
  };

  const handleInputBlur = (e) => {
    const inputRoot = e.target.closest(".MuiOutlinedInput-root");
    if (inputRoot) {
      const fieldset = inputRoot.querySelector("fieldset");
      if (fieldset) {
        fieldset.style.borderColor = "#e5e7eb";
      }
    }
  };

  const checkboxOption = (type, value, checked, label) => (
    <FormControlLabel
      key={`${type}-${value}`}
      control={
        <Checkbox
          checked={checked}
          onChange={() => toggleFilter(type, value)}
          className="b2b-checkbox"
          color="default"
          style={{
            color: checked ? theme.primary : "#9ca3af",
          }}
          icon={
            <span
              className="sidebar-checkbox-icon sidebar-checkbox-icon--off"
              style={{ borderColor: "#d1d5db" }}
            />
          }
          checkedIcon={
            <span
              className="sidebar-checkbox-icon sidebar-checkbox-icon--on"
              style={{
                borderColor: theme.primary,
                backgroundColor: theme.primary,
              }}
            >
              ✓
            </span>
          }
        />
      }
      label={label}
      className="filter-option"
    />
  );

  return (
    <Box className="sidebar-container">
      <Box className="sidebar-search">
        <Typography className="sidebar-section-title">
          Filtrar Pedido
        </Typography>
        <Box className="search-input-wrapper">
          <SearchIcon className="search-icon" />
          <TextField
            fullWidth
            placeholder="Buscar Ref. ou Produto"
            value={productSearchTerm}
            onChange={(e) => setProductSearchTerm(e.target.value)}
            className="search-input"
            variant="outlined"
            InputProps={{
              className: "search-input-inner",
              startAdornment: null,
            }}
            inputProps={{
              style: { paddingLeft: "36px" },
            }}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
          />
        </Box>
      </Box>

      <Box className="sidebar-filters">
        <FilterSection
          title="Vitrine"
          icon={StoreIcon}
          searchPlaceholder="Buscar nome ou código..."
          searchTerm={vitrineSearchTerm}
          onSearchChange={setVitrineSearchTerm}
          onInputFocus={handleInputFocus}
          onInputBlur={handleInputBlur}
          emptyMessage={
            filteredVitrines.length === 0 ? "Nenhuma vitrine encontrada." : null
          }
        >
          {filteredVitrines.map((v) =>
            checkboxOption(
              "vitrine",
              v,
              selectedVitrines.has(v),
              formatVitrineFilterLabel(v, vitrinesMeta),
            ),
          )}
        </FilterSection>

        <FilterSection
          title="Categoria"
          icon={LayersIcon}
          searchPlaceholder="Buscar categoria..."
          searchTerm={categorySearchTerm}
          onSearchChange={setCategorySearchTerm}
          onInputFocus={handleInputFocus}
          onInputBlur={handleInputBlur}
          emptyMessage={
            filteredCategories.length === 0
              ? "Nenhuma categoria encontrada."
              : null
          }
        >
          {filteredCategories.map((cat) =>
            checkboxOption(
              "category",
              cat,
              selectedCategories.has(cat),
              cat,
            ),
          )}
        </FilterSection>

        <FilterSection
          title="Nome comercial"
          icon={WorkIcon}
          searchPlaceholder="Buscar nome comercial..."
          searchTerm={commercialSearchTerm}
          onSearchChange={setCommercialSearchTerm}
          onInputFocus={handleInputFocus}
          onInputBlur={handleInputBlur}
          emptyMessage={
            filteredCommercialNames.length === 0
              ? "Nenhum nome comercial encontrado."
              : null
          }
        >
          {filteredCommercialNames.map((cn) =>
            checkboxOption(
              "commercial",
              cn,
              selectedCommercialNames.has(cn),
              cn,
            ),
          )}
        </FilterSection>

        <FilterSection
          title="Tamanho"
          icon={StraightenIcon}
          searchPlaceholder="Buscar tamanho..."
          searchTerm={sizeSearchTerm}
          onSearchChange={setSizeSearchTerm}
          onInputFocus={handleInputFocus}
          onInputBlur={handleInputBlur}
          emptyMessage={
            filteredSizes.length === 0 ? "Nenhum tamanho encontrado." : null
          }
        >
          {filteredSizes.map((size) =>
            checkboxOption("size", size, selectedSizes.has(size), size),
          )}
        </FilterSection>

        <FilterSection
          title="Cor"
          icon={PaletteIcon}
          searchPlaceholder="Buscar cor..."
          searchTerm={colorSearchTerm}
          onSearchChange={setColorSearchTerm}
          onInputFocus={handleInputFocus}
          onInputBlur={handleInputBlur}
          emptyMessage={
            filteredColors.length === 0 ? "Nenhuma cor encontrada." : null
          }
        >
          {filteredColors.map((color) =>
            checkboxOption("color", color, selectedColors.has(color), color),
          )}
        </FilterSection>
      </Box>
    </Box>
  );
}
